import { Component, signal, HostListener, ElementRef, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * InfoTooltipComponent - A mobile-friendly info tooltip/popover
 *
 * Desktop: Shows on hover (mouseenter/mouseleave)
 * Mobile: Shows on click/tap with backdrop to close
 *
 * Usage:
 * ```html
 * <lib-info-tooltip>
 *   <p>Your tooltip content here</p>
 * </lib-info-tooltip>
 * ```
 */
@Component({
  selector: 'lib-info-tooltip',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host {
      display: inline-flex;
      position: relative;
    }
    .info-tooltip-content {
      animation: scale-in 0.15s ease-out;
    }
    @keyframes scale-in {
      from {
        opacity: 0;
        transform: scale(0.95) translateY(-4px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }
  `],
  template: `
    <!-- Info icon button -->
    <button
      type="button"
      class="inline-flex items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1"
      [class]="buttonClass()"
      [style]="buttonStyle()"
      (click)="toggle($event)"
      (mouseenter)="showOnHover()"
      (mouseleave)="scheduleHide()"
      [attr.aria-expanded]="isVisible()"
      aria-haspopup="true"
    >
      <!-- Heroicon: information-circle (outline) -->
      <svg
        [class]="iconClass()"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
        />
      </svg>
    </button>

    <!-- Tooltip content -->
    @if (isVisible()) {
      <!-- Mobile backdrop (click to close) -->
      <div
        class="fixed inset-0 z-40 md:hidden"
        (click)="hide()"
        aria-hidden="true"
      ></div>

      <!-- Tooltip popover -->
      <div
        class="info-tooltip-content absolute z-50 mt-1 w-64 sm:w-72 rounded-lg border shadow-lg p-3 text-sm"
        [class]="contentPositionClass()"
        [style]="contentStyle()"
        role="tooltip"
        (mouseenter)="onTooltipMouseEnter()"
        (mouseleave)="onTooltipMouseLeave()"
      >
        <ng-content></ng-content>
      </div>
    }
  `,
})
export class InfoTooltipComponent {
  private readonly elementRef = inject(ElementRef);

  /** Size of the icon: 'sm' (16px), 'md' (20px), 'lg' (24px) */
  size = input<'sm' | 'md' | 'lg'>('sm');

  /** Position of the tooltip: 'bottom-left', 'bottom-right', 'bottom-center' */
  position = input<'bottom-left' | 'bottom-right' | 'bottom-center'>('bottom-left');

  /** Custom button class override */
  customButtonClass = input<string>('');

  /** Whether the tooltip is currently visible */
  isVisible = signal(false);

  private hideTimeout: ReturnType<typeof setTimeout> | null = null;
  private isTooltipHovered = false;
  private isTouchDevice = false;

  constructor() {
    // Detect touch device
    if (typeof window !== 'undefined') {
      this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }
  }

  buttonClass(): string {
    if (this.customButtonClass()) {
      return this.customButtonClass();
    }
    return 'hover:bg-black/5';
  }

  buttonStyle(): string {
    return 'color: var(--color-muted-foreground, #64748b);';
  }

  iconClass(): string {
    const sizeValue = this.size();
    switch (sizeValue) {
      case 'lg': return 'w-6 h-6';
      case 'md': return 'w-5 h-5';
      case 'sm':
      default: return 'w-4 h-4';
    }
  }

  contentPositionClass(): string {
    const positionValue = this.position();
    switch (positionValue) {
      case 'bottom-right': return 'right-0 top-full';
      case 'bottom-center': return 'left-1/2 -translate-x-1/2 top-full';
      case 'bottom-left':
      default: return 'left-0 top-full';
    }
  }

  contentStyle(): string {
    return `
      background-color: var(--color-card, #fff);
      border-color: var(--color-border, #e2e8f0);
      color: var(--color-foreground, #1e293b);
    `;
  }

  toggle(event: Event): void {
    event.stopPropagation();
    this.clearHideTimeout();

    if (this.isVisible()) {
      this.hide();
    } else {
      this.show();
    }
  }

  show(): void {
    this.clearHideTimeout();
    this.isVisible.set(true);
  }

  hide(): void {
    this.isVisible.set(false);
  }

  showOnHover(): void {
    // On touch devices, don't show on hover (use click instead)
    if (this.isTouchDevice) return;

    this.clearHideTimeout();
    this.show();
  }

  scheduleHide(): void {
    // On touch devices, don't auto-hide (use backdrop click instead)
    if (this.isTouchDevice) return;

    this.hideTimeout = setTimeout(() => {
      if (!this.isTooltipHovered) {
        this.hide();
      }
    }, 150);
  }

  onTooltipMouseEnter(): void {
    this.isTooltipHovered = true;
    this.clearHideTimeout();
  }

  onTooltipMouseLeave(): void {
    this.isTooltipHovered = false;
    this.scheduleHide();
  }

  private clearHideTimeout(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  /** Close tooltip when clicking outside */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isVisible() && !this.elementRef.nativeElement.contains(event.target)) {
      this.hide();
    }
  }

  /** Close tooltip on Escape key */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isVisible()) {
      this.hide();
    }
  }
}
